/**
 * Storage layer - main entry point
 * Provides unified storage API with CloudStorage/localStorage fallback
 */

import type { StorageAdapter } from './adapter';

import { CloudStorageAdapter, HybridStorageAdapter, LocalStorageAdapter } from './adapter';
import { createCloudStorageApi } from './cloudStorageApi';
import { IS_CLOUD_STORAGE_ENABLED } from './constants';

// Singleton instance
let storageAdapter: StorageAdapter | undefined;

/**
 * Initialize storage adapter
 * Should be called early in app initialization
 */
export async function initializeStorage(): Promise<void> {
  if (storageAdapter) {
    return; // Already initialized
  }

  if (!IS_CLOUD_STORAGE_ENABLED) {
    // eslint-disable-next-line no-console
    console.log('[Storage] CloudStorage disabled, using localStorage only');
    storageAdapter = new LocalStorageAdapter();
    return;
  }

  const cloudStorageApi = createCloudStorageApi();

  if (cloudStorageApi.isAvailable()) {
    // eslint-disable-next-line no-console
    console.log('[Storage] Using CloudStorage with localStorage fallback');
    storageAdapter = new HybridStorageAdapter(cloudStorageApi);
  } else {
    // eslint-disable-next-line no-console
    console.log('[Storage] CloudStorage not available, using localStorage only');
    storageAdapter = new LocalStorageAdapter();
  }
}

/**
 * Get storage adapter instance
 * Throws if not initialized
 */
export function getStorageAdapter(): StorageAdapter {
  if (!storageAdapter) {
    throw new Error('Storage not initialized. Call initializeStorage() first.');
  }
  return storageAdapter;
}

/**
 * Set storage adapter (mainly for testing)
 */
export function setStorageAdapter(adapter: StorageAdapter): void {
  storageAdapter = adapter;
}

/**
 * Check if storage is initialized
 */
export function isStorageInitialized(): boolean {
  return Boolean(storageAdapter);
}

/**
 * Debug function: Clear all CloudStorage data
 * WARNING: This will delete all synced data!
 */
export async function clearCloudStorage(): Promise<void> {
  // Намеренно не берём глобальный адаптер: он локальный, пока CloudStorage выключен,
  // а вычистить надо именно облако - там осталось состояние от прежней схемы
  const cloudStorageApi = createCloudStorageApi();
  if (!cloudStorageApi.isAvailable()) {
    throw new Error('CloudStorage is not available in this environment');
  }

  const adapter = new CloudStorageAdapter(cloudStorageApi);

  // eslint-disable-next-line no-console
  console.log('[Storage] Clearing all CloudStorage data...');

  try {
    const allKeys = await adapter.getKeys();
    const ttKeys = allKeys.filter((key) => key.startsWith('tt_'));

    // eslint-disable-next-line no-console
    console.log(`[Storage] Found ${ttKeys.length} keys to delete:`, ttKeys);

    for (const key of ttKeys) {
      await adapter.removeItem(key);
    }

    // eslint-disable-next-line no-console
    console.log('[Storage] CloudStorage cleared successfully');
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[Storage] Failed to clear CloudStorage', error);
    throw error;
  }
}

/**
 * Debug function: Clear all localStorage data
 * WARNING: This will delete all local data!
 */
export function clearLocalStorage(): void {
  // eslint-disable-next-line no-console
  console.log('[Storage] Clearing all localStorage data...');

  const allKeys = Object.keys(localStorage);
  const ttKeys = allKeys.filter((key) => key.startsWith('tt_') || key.startsWith('tt:'));

  // eslint-disable-next-line no-console
  console.log(`[Storage] Found ${ttKeys.length} keys to delete:`, ttKeys);

  for (const key of ttKeys) {
    localStorage.removeItem(key);
  }

  // eslint-disable-next-line no-console
  console.log('[Storage] localStorage cleared successfully');
}

/**
 * Debug function: Clear ALL storage (CloudStorage + localStorage)
 * WARNING: This will delete all data and log you out!
 */
export async function clearAllStorage(): Promise<void> {
  // eslint-disable-next-line no-console
  console.log('[Storage] Clearing ALL storage...');

  clearLocalStorage();
  await clearCloudStorage();

  // eslint-disable-next-line no-console
  console.log('[Storage] All storage cleared. Please reload the page.');
}

// Expose debug functions to window for console access
if (typeof window !== 'undefined') {
  (window as any).debugStorage = {
    clearCloudStorage,
    clearLocalStorage,
    clearAllStorage,
    getKeys: async () => {
      const adapter = getStorageAdapter();
      return adapter.getKeys();
    },
    getAllData: async () => {
      const adapter = getStorageAdapter();
      const keys = await adapter.getKeys();
      const data: Record<string, string | undefined> = {};
      for (const key of keys) {
        data[key] = await adapter.getItem(key);
      }
      return data;
    },
  };
}

// Re-export types and utilities
export type { StorageAdapter } from './adapter';
export type { CloudStorageApi } from './cloudStorageApi';
export { LocalStorageAdapter, HybridStorageAdapter, CloudStorageAdapter } from './adapter';
export { createCloudStorageApi, isCloudStorageAvailable } from './cloudStorageApi';
export { setChunkedItem, getChunkedItem, removeChunkedItem } from './chunking';
export type { ChunkedDataMeta } from './chunking';
export * from './constants';
